import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { IpoTranslatePipe } from '@ip-only/front-end-library';

import { Feature, LineString } from 'geojson';
import { Observable } from 'rxjs';
import {
  DistributionPointProperties,
  SpanProperties,
} from '../../../../../span-progress-app/span-progress-entities';
import { ErrorInformationComponent } from '../../../../error-information/error-information.component';
import { FeedbackAlertComponent } from '../../../../feedback-alert/feedback-alert.component';
import { SharedModule } from '../../../../shared.module';
import { RolloutProgressReportEntities } from '../../../service/entities';
import { InspectionReportInitService } from '../../../service/initial-values/inspection-report-init.service';
import { ReportInfoService } from '../../../service/report-info/report-info.service';
import { RolloutProgressReportsService } from '../../../service/reports/rollout-progress-reports.service';
import { ReportViewBaseComponent } from '../../report-view-base/report-view-base.component';
import { ReportCommentComponent } from '../report-comment/report-comment.component';
import { ReportGroupsV2Component } from '../report-groups-v2/report-groups-v2.component';
import { ReportInfoV2Component } from '../report-info-v2/report-info-v2.component';

@Component({
  selector: 'app-report-view-v2',
  imports: [
    CommonModule,
    SharedModule,
    ReportGroupsV2Component,
    ErrorInformationComponent,
    FeedbackAlertComponent,
    IpoTranslatePipe,
    ReportInfoV2Component,
    ReportCommentComponent,
  ],
  templateUrl: './report-view-v2.component.html',
  styleUrl: './report-view-v2.component.scss',
})
export class ReportViewV2Component extends ReportViewBaseComponent {
  private readonly initService = inject(InspectionReportInitService);
  private readonly reportInfoService = inject(ReportInfoService);
  private readonly reportsService = inject(RolloutProgressReportsService);

  pointProperties = input<DistributionPointProperties>();
  spanPropertiesArray = input<Feature<LineString, SpanProperties>[]>();

  reportData = this.reportsService.storedReportData;
  reportInfoData = signal<RolloutProgressReportEntities.v3.ReportInfoData>(
    {} as RolloutProgressReportEntities.v3.ReportInfoData,
  );

  typeId = this.initService.getInitValueProperty('typeId');
  markerId = this.initService.getInitValueProperty('markerId');

  inspectionReportGroupsDataQueryLoadingState$: Observable<RolloutProgressReportEntities.v3.InspectionGroupsAndReportLoadingState> =
    this.combinedService.getInspectionGroupsAndReportData$();

  constructor() {
    super();
    effect(() => {
      untracked(() => this.spanPropertiesArray());

      if (this.typeId && this.markerId) {
        this.getReportInfoData();
      }
    });
    effect(() => {
      if (this.reportInfoData()) {
        this.initService.setIsReportLoading(false);
      }
    });
  }

  getReportInfoData(): void {
    if (
      this.typeId === RolloutProgressReportEntities.v3.MarkerTypes.SPAN &&
      this.spanPropertiesArray()
    ) {
      this.setMultiSpanReportData();
      return;
    }

    this.setPointReportData();
  }

  setMultiSpanReportData = (): void => {
    const spanProperties = this.spanPropertiesArray();
    if (
      this.typeId === RolloutProgressReportEntities.v3.MarkerTypes.SPAN &&
      spanProperties
    ) {
      const spansInReport = this.findSpansInMultiSpanReport();
      const reportInfoData =
        this.createReportInfoDataForMultiSpan(spansInReport);
      this.setReportInfoDataWhenUpdated(reportInfoData);
    }
  };

  setPointReportData = () => {
    const address = this.pointProperties()?.telematorId
      ? `${this.pointProperties()?.telematorId} (${this.pointProperties()?.name})`
      : `${this.pointProperties()?.name}`;

    this.reportInfoData.set(
      this.reportInfoService.createReportInfoDataForPoint(
        this.reportData(),
        address,
      ),
    );
  };

  checkQueryParamValidity() {
    if (this.typeId && this.markerId) {
      return this.checkTypeIdValidity();
    } else {
      return false;
    }
  }

  checkTypeIdValidity() {
    if (
      Object.values(RolloutProgressReportEntities.v3.MarkerTypes).includes(
        this.typeId,
      )
    ) {
      return true;
    } else {
      return false;
    }
  }

  private readonly findSpansInMultiSpanReport = (): SpanProperties[] =>
    this.spanPropertiesArray()
      ?.filter(({ properties }) =>
        this.reportData().spans?.includes(properties.spanId),
      )
      .map((span) => span.properties) as SpanProperties[];

  private readonly createReportInfoDataForMultiSpan = (
    spansInReport: SpanProperties[],
  ): RolloutProgressReportEntities.v3.ReportInfoData =>
    this.reportInfoService.createReportInfoDataForMultiSpan(
      this.reportData(),
      spansInReport,
    );

  private readonly setReportInfoDataWhenUpdated = (
    reportInfoData: RolloutProgressReportEntities.v3.ReportInfoData,
  ): void => {
    if (
      JSON.stringify(this.reportInfoData()) !== JSON.stringify(reportInfoData)
    ) {
      this.reportInfoData.set(reportInfoData);
    }
  };
}
